import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/search-input";
import { Slider } from "@/components/ui/slider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Check,
  Loader2,
  Plus,
  Undo2,
  X,
  RefreshCw,
  History,
  Calendar,
  User,
  FileText,
} from "lucide-react";
import {
  MacroInputsCard,
  MacronutrientTargetsProvider,
  TargetEnergyCard,
} from "../components/MacronutrientTargetsV2";
import { useNutritionEvaluationContext } from "../NutritionEvaluationContext";
import {
  useMealStrategy,
  useUpdateMealStrategy,
  useMealStrategyHistory,
  useNutritionTarget,
  useUpdateNutritionTarget,
  useNutritionProfile,
  useHealthProfile,
  useSpecialFeedConsiderations,
  useSearchSpecialFeedOptions,
  useUpdateSpecialFeedConsiderations,
} from "@/hooks/useNutritionEvaluation";
import { useDirtyFlag, useDirtyHighlight } from "@/hooks/useUnsavedChanges";
import type {
  NutritionTarget,
  SpecialFeedConsideration,
  SpecialFeedConsiderationOption,
  MealStrategyHistory,
} from "@/types/nutrition";
import { cn } from "@/lib/utils";

// =============================================================================
// Weight Unit Helpers
// =============================================================================

type WeightUnit = "kgs" | "lbs";
const KG_TO_LB = 2.20462;
const kgToLb = (kg: number) => kg * KG_TO_LB;
const lbToKg = (lb: number) => lb / KG_TO_LB;

// Slider canonical bounds (kg). Display labels are converted per the active unit.
const TARGET_WEIGHT_MIN_KG = 30;
const TARGET_WEIGHT_MAX_KG = 200;
const TARGET_WEIGHT_STEP_KG = 0.5;

// Hide native number-input spinner arrows. Inline because the shared Input
// primitive shouldn't bake this in for everyone.
const NO_SPINNER =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

// Strip leading/embedded minus signs so paste-from-elsewhere ("-50") and
// keyboard "-" entries can't sneak past the native number input.
const stripNegative = (s: string) => s.replace(/-/g, "");

const fmtWeight = (n: number, dp = 1) => {
  const r = parseFloat(n.toFixed(dp));
  return Number.isFinite(r) ? r.toString() : "";
};

interface UnitSwitcherProps {
  options: readonly WeightUnit[];
  value: WeightUnit;
  onChange: (v: WeightUnit) => void;
  disabled?: boolean;
}

const UnitSwitcher: React.FC<UnitSwitcherProps> = ({
  options,
  value,
  onChange,
  disabled,
}) => (
  <div className="inline-flex items-center rounded-full bg-muted p-0.5">
    {options.map((opt) => (
      <button
        type="button"
        key={opt}
        tabIndex={-1}
        onClick={() => onChange(opt)}
        disabled={disabled || value === opt}
        className={cn(
          "z-hint rounded-full px-1.5 py-0.5 transition-colors disabled:opacity-100",
          value === opt
            ? "bg-card text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        {opt}
      </button>
    ))}
  </div>
);

// =============================================================================
// Nutrient Conversion Utilities
// =============================================================================

const carbsGramsToKcal = (grams: number) => Math.max(0, grams * 4);
const proteinsGramsToKcal = (grams: number) => grams * 4;
const fatsGramsToKcal = (grams: number) => grams * 9;
const fiberGramsToKcal = (grams: number) => grams * 2;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// =============================================================================
// PillTag — interactive add / remove pill (uses shared Badge tokens)
// =============================================================================

interface PillTagProps {
  text: string;
  variant: "add" | "remove";
  onTap: () => void;
  disabled?: boolean;
}

const PillTag: React.FC<PillTagProps> = ({
  text,
  variant,
  onTap,
  disabled,
}) => {
  const isAdd = variant === "add";
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 z-caption transition-colors",
        "border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        isAdd
          ? "border-border bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground"
          : "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
      )}
    >
      {text}
      {isAdd ? <Plus className="size-3" /> : <X className="size-3" />}
    </button>
  );
};

// =============================================================================
// Special Feed Consideration Card (Tube Feeding only)
// =============================================================================

const SpecialFeedConsiderationCard: React.FC = () => {
  const { appointmentId, isCompleted, isTubeFeeding } =
    useNutritionEvaluationContext();
  const { data: considerations = [], isLoading } =
    useSpecialFeedConsiderations(appointmentId);
  const searchMutation = useSearchSpecialFeedOptions();
  const updateConsiderations =
    useUpdateSpecialFeedConsiderations(appointmentId);

  const [showDialog, setShowDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    SpecialFeedConsiderationOption[]
  >([]);
  const [localConsiderations, setLocalConsiderations] = useState<
    SpecialFeedConsideration[]
  >([]);

  useEffect(() => {
    setLocalConsiderations(considerations);
  }, [considerations]);

  if (!isTubeFeeding) return null;

  const handleOpenDialog = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowDialog(true);
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      const results = await searchMutation.mutateAsync(query);
      const selectedIds = new Set(
        localConsiderations.map((c) => c.consideration)
      );
      setSearchResults(results.filter((r) => !selectedIds.has(r.id)));
    } else {
      setSearchResults([]);
    }
  };

  const handleAddConsideration = async (
    option: SpecialFeedConsiderationOption
  ) => {
    if (localConsiderations.some((c) => c.consideration === option.id)) return;

    const newConsideration: SpecialFeedConsideration = {
      id: `temp-${option.id}`,
      consideration: option.id,
      consideration_details: {
        id: option.id,
        consideration: option.consideration,
      },
    };
    const updatedConsiderations = [...localConsiderations, newConsideration];
    setLocalConsiderations(updatedConsiderations);
    setSearchResults((prev) => prev.filter((r) => r.id !== option.id));

    const ids = updatedConsiderations.map((c) => c.consideration);
    await updateConsiderations.mutateAsync(ids);
  };

  const handleRemoveConsideration = async (item: SpecialFeedConsideration) => {
    if (isCompleted || updateConsiderations.isPending) return;

    const removedOption: SpecialFeedConsiderationOption | null =
      item.consideration_details
        ? {
            id: item.consideration_details.id,
            consideration: item.consideration_details.consideration,
          }
        : null;

    const updatedConsiderations = localConsiderations.filter(
      (c) => c.id !== item.id
    );
    setLocalConsiderations(updatedConsiderations);

    if (removedOption && searchQuery.length >= 2) {
      const matchesSearch = removedOption.consideration
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      if (matchesSearch) {
        setSearchResults((prev) => {
          if (prev.some((r) => r.id === removedOption.id)) return prev;
          return [...prev, removedOption];
        });
      }
    }

    const ids = updatedConsiderations.map((c) => c.consideration);
    await updateConsiderations.mutateAsync(ids);
  };

  if (isLoading) {
    return (
      <Widget>
        <WidgetContent className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </WidgetContent>
      </Widget>
    );
  }

  return (
    <>
      <Widget>
        <WidgetHeader
          title="Special Feed Considerations"
          onAdd={isCompleted ? undefined : handleOpenDialog}
        />
        <WidgetContent>
          {localConsiderations.length === 0 ? (
            <EmptyState
              title="No special considerations added"
              variant="compact"
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {localConsiderations.map((c) => (
                <PillTag
                  key={c.id}
                  text={
                    c.consideration_details?.consideration ||
                    `#${c.consideration}`
                  }
                  variant="remove"
                  onTap={() => handleRemoveConsideration(c)}
                  disabled={isCompleted || updateConsiderations.isPending}
                />
              ))}
            </div>
          )}
        </WidgetContent>
      </Widget>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 shrink-0">
            <DialogTitle className="z-h2 text-foreground">
              Add Special Feed Consideration
            </DialogTitle>
          </DialogHeader>
          <Separator />

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-border shrink-0 space-y-3">
              <span className="z-eyebrow text-muted-foreground">
                Already Added
              </span>
              <div className="rounded-lg border border-border bg-muted/30 p-3 max-h-[120px] overflow-y-auto scrollbar-thin">
                {localConsiderations.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {localConsiderations.map((item) => (
                      <PillTag
                        key={item.id}
                        text={
                          item.consideration_details?.consideration ||
                          `#${item.consideration}`
                        }
                        variant="remove"
                        onTap={() => handleRemoveConsideration(item)}
                        disabled={isCompleted || updateConsiderations.isPending}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="z-hint text-muted-foreground text-center py-1">
                    No considerations added yet
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-b border-border shrink-0">
              <SearchInput
                placeholder="Search special feed considerations..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                isLoading={searchMutation.isPending}
                autoFocus
              />
            </div>

            <ScrollArea className="flex-1">
              <div className="p-6">
                {searchResults.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {searchResults.map((option) => (
                      <PillTag
                        key={option.id}
                        text={option.consideration}
                        variant="add"
                        onTap={() => handleAddConsideration(option)}
                        disabled={updateConsiderations.isPending}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="z-body text-muted-foreground text-center py-8">
                    {searchQuery.length >= 2
                      ? "No results found"
                      : "Type at least 2 characters to search"}
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// =============================================================================
// Meal Strategy History Dialog
// =============================================================================

interface MealStrategyHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
}

const MealStrategyHistoryDialog: React.FC<MealStrategyHistoryDialogProps> = ({
  open,
  onOpenChange,
  appointmentId,
}) => {
  const { data: history = [], isLoading } =
    useMealStrategyHistory(appointmentId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[80vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <History className="size-5 text-muted-foreground" />
            Meal Strategy — History
          </DialogTitle>
        </DialogHeader>
        <Separator />

        <ScrollArea className="flex-1">
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : history.length === 0 ? (
              <p className="z-body text-center text-muted-foreground py-12">
                No history available
              </p>
            ) : (
              <div className="space-y-6">
                {history.map((item: MealStrategyHistory, index: number) => (
                  <div key={item.id || index} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-border" />
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                        <Calendar className="size-3 text-primary" />
                        <span className="z-caption text-primary">
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-border" />
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="neutral" className="z-caption">
                        <FileText className="size-3" />
                        {item.zarity_appointment_id}
                      </Badge>
                      <Badge variant="neutral" className="z-caption">
                        <User className="size-3" />
                        {item.doctor_name}
                      </Badge>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/30 p-4">
                      <ol className="space-y-2">
                        {(item.meal_strategies || item.strategies || []).map(
                          (strategy: string, idx: number) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3 z-body text-foreground"
                            >
                              <span className="text-muted-foreground min-w-[1.25rem]">
                                {idx + 1}.
                              </span>
                              <span className="flex-1">{strategy}</span>
                            </li>
                          )
                        )}
                      </ol>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="p-6 pt-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Add Meal Strategy Dialog
// =============================================================================

interface AddMealStrategyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  strategies: string[];
  onStrategiesChange: (strategies: string[]) => void;
  onSave: (updated?: string[]) => void;
  isPending: boolean;
}

const AddMealStrategyDialog: React.FC<AddMealStrategyDialogProps> = ({
  open,
  onOpenChange,
  strategies,
  onStrategiesChange,
  onSave,
  isPending,
}) => {
  const [newStrategy, setNewStrategy] = useState("");
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewStrategy(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    const maxHeight = 80;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  };

  const handleAdd = () => {
    if (newStrategy.trim()) {
      const updated = [...strategies, newStrategy.trim()];
      onStrategiesChange(updated);
      setNewStrategy("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      onSave(updated);
    }
  };

  const handleRemove = (index: number) => {
    const updated = strategies.filter((_, i) => i !== index);
    onStrategiesChange(updated);
    onSave(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="z-h2 text-foreground">
            Add Meal Strategy
          </DialogTitle>
        </DialogHeader>
        <Separator />

        <div className="p-6 space-y-5">
          <div className="space-y-2">
            <span className="z-eyebrow text-muted-foreground">
              Already Added
            </span>
            <div className="rounded-lg border border-border bg-muted/30 p-3 max-h-72 overflow-y-auto scrollbar-thin">
              {strategies.length > 0 ? (
                <ol className="space-y-1.5">
                  {strategies.map((strategy, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-3 px-3 py-2 bg-card rounded-md border border-border animate-in fade-in zoom-in-95 duration-200"
                    >
                      <span className="z-body text-muted-foreground shrink-0">
                        {index + 1}.
                      </span>
                      <span className="flex-1 z-body text-foreground leading-tight break-words min-w-0">
                        {strategy}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemove(index)}
                      >
                        <X className="size-3.5" />
                      </Button>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="z-hint text-muted-foreground text-center py-1">
                  No strategies added yet
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2 items-end rounded-lg border border-border bg-muted/30 p-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 transition-colors">
            <textarea
              ref={textareaRef}
              placeholder="Type a strategy here..."
              value={newStrategy}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              rows={1}
              className="flex-1 min-h-9 max-h-20 bg-transparent border-none resize-none px-2 py-1.5 z-body text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 scrollbar-thin"
              autoFocus
            />
            <Button
              size="icon"
              onClick={handleAdd}
              disabled={!newStrategy.trim() || isPending}
              className="size-8 shrink-0"
            >
              <Plus className="size-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Meal Strategy Card
// =============================================================================

const MealStrategyCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: strategy, isLoading } = useMealStrategy(appointmentId);
  const updateStrategy = useUpdateMealStrategy(appointmentId);

  const [strategies, setStrategies] = useState<string[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const syncRef = React.useRef(false);

  useEffect(() => {
    if (strategy?.strategies && !syncRef.current) {
      setStrategies(strategy.strategies);
      syncRef.current = true;
    }
  }, [strategy]);

  const handleRemoveStrategy = (index: number) => {
    const updated = strategies.filter((_, i) => i !== index);
    setStrategies(updated);
    updateStrategy.mutate({ strategies: updated, existingId: strategy?.id });
  };

  const handleSave = (updated?: string[]) => {
    updateStrategy.mutate({
      strategies: updated || strategies,
      existingId: strategy?.id,
    });
  };

  if (isLoading) {
    return (
      <Widget>
        <WidgetContent className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </WidgetContent>
      </Widget>
    );
  }

  return (
    <>
      <Widget>
        <WidgetHeader
          title="Meal Strategy"
          hasInfo
          onAdd={isCompleted ? undefined : () => setShowAddDialog(true)}
          extras={
            strategies.length > 0 ? (
              <button
                type="button"
                className="flex items-center justify-center size-5 rounded-md bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
                onClick={() => setShowHistoryDialog(true)}
                title="View History"
              >
                <RefreshCw className="size-3" />
              </button>
            ) : null
          }
        />
        <WidgetContent>
          {strategies.length === 0 ? (
            <EmptyState title="No meal strategies added" variant="compact" />
          ) : (
            <ol className="divide-y divide-border">
              {strategies.map((s, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <span className="z-body text-muted-foreground w-6">
                    {i + 1}.
                  </span>
                  <span className="flex-1 z-body text-foreground">{s}</span>
                  {!isCompleted && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveStrategy(i)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </WidgetContent>
      </Widget>

      <AddMealStrategyDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        strategies={strategies}
        onStrategiesChange={setStrategies}
        onSave={handleSave}
        isPending={updateStrategy.isPending}
      />

      <MealStrategyHistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        appointmentId={appointmentId}
      />
    </>
  );
};

// =============================================================================
// Weight / Energy Targets Card
// =============================================================================

const WeightEnergyTargetsCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: healthProfile } = useHealthProfile(appointmentId);
  const { data: target, isLoading } = useNutritionTarget(appointmentId);
  const updateTarget = useUpdateNutritionTarget(appointmentId);

  const [unit, setUnit] = useState<WeightUnit>("kgs");
  const [targetWeight, setTargetWeight] = useState("");
  const syncRef = React.useRef(false);

  const currentWeightKg = healthProfile?.weight_in_kgs || 0;

  // Baseline (display string for the persisted target weight in the current unit).
  // Used for dirty detection via string equality.
  const baseline = useMemo(() => {
    if (target?.weight == null) return "";
    return unit === "lbs"
      ? fmtWeight(kgToLb(target.weight))
      : fmtWeight(target.weight);
  }, [target?.weight, unit]);

  // Gate one-shot hydration on first query resolve, not on target truthiness — sibling card creating the target row would otherwise re-fire and wipe drafts here.
  useEffect(() => {
    if (isLoading || syncRef.current) return;
    if (target) {
      setTargetWeight(target.weight ? fmtWeight(target.weight) : "");
    }
    syncRef.current = true;
  }, [isLoading, target]);

  const switchUnit = (next: WeightUnit) => {
    if (next === unit) return;
    setTargetWeight((prev) => {
      const n = parseFloat(prev);
      if (!Number.isFinite(n)) return prev;
      return next === "lbs" ? fmtWeight(kgToLb(n)) : fmtWeight(lbToKg(n));
    });
    setUnit(next);
  };

  const targetWeightNum = parseFloat(targetWeight);
  const hasTargetWeight =
    Number.isFinite(targetWeightNum) && targetWeightNum > 0;
  const hasCurrentWeight = currentWeightKg > 0;

  // Range guard — input itself doesn't clamp (only the slider does), so
  // validate canonical kg here regardless of which unit is shown.
  const targetWeightInKgs = hasTargetWeight
    ? unit === "lbs"
      ? lbToKg(targetWeightNum)
      : targetWeightNum
    : 0;
  const isWeightInRange =
    !hasTargetWeight ||
    (targetWeightInKgs >= TARGET_WEIGHT_MIN_KG &&
      targetWeightInKgs <= TARGET_WEIGHT_MAX_KG);

  const isDirty = !isCompleted && targetWeight !== baseline;
  useDirtyFlag(
    `weight-energy-target:${appointmentId}`,
    isDirty,
    "Target Weight"
  );
  const highlighted = useDirtyHighlight(isDirty);

  const revertTargetWeight = () => setTargetWeight(baseline);

  const handleSave = () => {
    if (!isDirty || !hasTargetWeight || !isWeightInRange) return;
    const weightInKgs =
      unit === "lbs" ? lbToKg(targetWeightNum) : targetWeightNum;
    const data: Partial<NutritionTarget> = {
      ...target,
      weight: parseFloat(weightInKgs.toFixed(2)),
    };
    delete data.id;
    updateTarget.mutate({ data, isNew: !target });
  };

  if (isLoading) {
    return (
      <Widget>
        <WidgetContent className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </WidgetContent>
      </Widget>
    );
  }

  const unitLabel = unit === "lbs" ? "Lbs" : "Kgs";

  // Delta = target − current, in the currently displayed unit.
  //   positive → patient needs to gain (deficit) → green +X
  //   negative → patient needs to lose (surplus) → red −X
  //   null     → can't be computed → em-dash
  const currentInUnit =
    unit === "lbs" ? kgToLb(currentWeightKg) : currentWeightKg;
  const delta =
    hasTargetWeight && hasCurrentWeight
      ? targetWeightNum - currentInUnit
      : null;
  const deltaLabel =
    delta === null
      ? "—"
      : `${delta > 0 ? "+" : delta < 0 ? "−" : ""}${Math.abs(delta).toFixed(1)} ${unitLabel}`;
  const deltaToneClass =
    delta === null
      ? "text-foreground"
      : delta > 0
        ? "text-success dark:text-success"
        : delta < 0
          ? "text-destructive dark:text-destructive"
          : "text-foreground";

  const currentDisplay =
    unit === "lbs"
      ? fmtWeight(kgToLb(currentWeightKg))
      : fmtWeight(currentWeightKg);

  // Slider operates on the canonical kg range; display labels follow the unit.
  const targetWeightInKg = hasTargetWeight
    ? unit === "lbs"
      ? lbToKg(targetWeightNum)
      : targetWeightNum
    : TARGET_WEIGHT_MIN_KG;
  const sliderValueKg = Math.min(
    TARGET_WEIGHT_MAX_KG,
    Math.max(TARGET_WEIGHT_MIN_KG, targetWeightInKg)
  );
  const weightMinDisplay =
    unit === "lbs"
      ? Math.round(kgToLb(TARGET_WEIGHT_MIN_KG))
      : TARGET_WEIGHT_MIN_KG;
  const weightMaxDisplay =
    unit === "lbs"
      ? Math.round(kgToLb(TARGET_WEIGHT_MAX_KG))
      : TARGET_WEIGHT_MAX_KG;

  return (
    <Widget>
      <WidgetHeader
        title="Target Weight"
        hasInfo
        extras={
          <div className="flex items-center gap-1.5">
            {hasCurrentWeight && (
              <Badge variant="neutral">
                <span className="text-muted-foreground">Current:</span>
                <span className="text-foreground">
                  {currentDisplay} {unitLabel}
                </span>
              </Badge>
            )}
            <Badge variant="neutral">
              <span className="text-muted-foreground">Difference:</span>
              <span className={deltaToneClass}>{deltaLabel}</span>
            </Badge>
          </div>
        }
      />
      <WidgetContent>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-3">
              <Label className="z-eyebrow text-muted-foreground">
                Target Weight
                <span className="text-destructive ml-0.5">*</span>
              </Label>
              <UnitSwitcher
                options={["kgs", "lbs"] as const}
                value={unit}
                onChange={switchUnit}
                disabled={isCompleted}
              />
            </div>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "rounded-3xl transition-shadow duration-300",
                  highlighted &&
                    "ring-4 ring-warning/50 ring-offset-2 ring-offset-background"
                )}
              >
                <div className="relative w-48">
                  <Input
                    type="number"
                    step="0.1"
                    min={weightMinDisplay}
                    max={weightMaxDisplay}
                    value={targetWeight}
                    onChange={(e) =>
                      setTargetWeight(stripNegative(e.target.value))
                    }
                    disabled={isCompleted}
                    aria-invalid={
                      (!hasTargetWeight || !isWeightInRange) && !isCompleted
                    }
                    className={cn(
                      "pr-12 z-numeric",
                      NO_SPINNER,
                      isDirty &&
                        "border-warning/60 bg-warning/10 focus-visible:border-warning focus-visible:ring-warning/30"
                    )}
                    placeholder="Enter value"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 z-caption text-muted-foreground pointer-events-none">
                    {unitLabel}
                  </span>
                </div>
              </div>
              {isDirty && !isCompleted && (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={revertTargetWeight}
                    disabled={updateTarget.isPending}
                    className="size-9 rounded-full text-muted-foreground hover:text-foreground"
                    aria-label="Discard target weight edit"
                  >
                    <Undo2 className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    onClick={handleSave}
                    disabled={
                      !hasTargetWeight ||
                      !isWeightInRange ||
                      updateTarget.isPending
                    }
                    className="size-9 rounded-full"
                    aria-label="Save target weight"
                  >
                    {updateTarget.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Check className="size-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
            {!hasTargetWeight && !isCompleted && (
              <p className="z-hint text-destructive">
                Required to send the nutrition report.
              </p>
            )}
            {hasTargetWeight && !isWeightInRange && !isCompleted && (
              <p className="z-hint text-destructive">
                Enter a value between {weightMinDisplay} and {weightMaxDisplay}{" "}
                {unitLabel}.
              </p>
            )}
          </div>

          <div className="w-80 max-w-full space-y-1.5">
            <Slider
              value={[sliderValueKg]}
              min={TARGET_WEIGHT_MIN_KG}
              max={TARGET_WEIGHT_MAX_KG}
              step={TARGET_WEIGHT_STEP_KG}
              onValueChange={(v) => {
                const nextKg = v[0] ?? TARGET_WEIGHT_MIN_KG;
                const display = unit === "lbs" ? kgToLb(nextKg) : nextKg;
                setTargetWeight(fmtWeight(display));
              }}
              disabled={isCompleted}
              aria-label="Target weight slider"
            />
            <div className="flex justify-between z-hint text-muted-foreground">
              <span>
                {weightMinDisplay} {unitLabel}
              </span>
              <span>
                {weightMaxDisplay} {unitLabel}
              </span>
            </div>
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Macronutrient Cell Primitives
// =============================================================================

// Borderless number input that fills a TableCell. Unit suffix sits on the right.
interface CellInputProps {
  value: string;
  onChange: (v: string) => void;
  unit: string;
  disabled?: boolean;
  hasError?: boolean;
  ariaLabel: string;
}

const CellInput: React.FC<CellInputProps> = ({
  value,
  onChange,
  unit,
  disabled,
  hasError,
  ariaLabel,
}) => (
  <div
    className={cn(
      "relative h-full w-full transition-colors",
      hasError && "bg-destructive/10"
    )}
  >
    <input
      type="number"
      min={0}
      step={0.1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder="0"
      aria-label={ariaLabel}
      aria-invalid={hasError || undefined}
      className={cn(
        "h-full w-full bg-transparent text-center z-body z-numeric text-foreground",
        "border-0 outline-none placeholder:text-muted-foreground/60",
        "focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40",
        "disabled:cursor-not-allowed disabled:opacity-60",
        unit && "pr-9",
        hasError && "text-destructive"
      )}
    />
    {unit && (
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 z-caption text-muted-foreground">
        {unit}
      </span>
    )}
  </div>
);

// Read-only display cell: muted when "—", uses numeric typography, optional unit suffix.
interface DisplayCellProps {
  value: string;
  unit?: string;
}

const DisplayCell: React.FC<DisplayCellProps> = ({ value, unit }) => {
  const isEmpty = !value || value === "—";
  return (
    <span
      className={cn(
        "z-body z-numeric",
        isEmpty ? "text-muted-foreground" : "text-foreground"
      )}
    >
      {isEmpty ? "—" : unit ? `${value} ${unit}` : value}
    </span>
  );
};

// =============================================================================
// Macronutrient Targets Card (LEGACY)
// Kept in source for one-line revert from <MacronutrientTargetsV2 />.
// =============================================================================

// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
const MacronutrientTargetsCard: React.FC = () => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: target, isLoading } = useNutritionTarget(appointmentId);
  const { data: profile } = useNutritionProfile(appointmentId);
  const updateTarget = useUpdateNutritionTarget(appointmentId);

  const [proteinGrams, setProteinGrams] = useState("");
  const [carbsGrams, setCarbsGrams] = useState("");
  const [fatGrams, setFatGrams] = useState("");
  const [fiberGrams, setFiberGrams] = useState("");

  const [energyValue, setEnergyValue] = useState("0");
  const [proteinE, setProteinE] = useState("—");
  const [carbsE, setCarbsE] = useState("—");
  const [fatE, setFatE] = useState("—");
  const [fiberE, setFiberE] = useState("—");
  const [energyE, setEnergyE] = useState("—");

  const [proteinRDA, setProteinRDA] = useState("—");
  const [carbsRDA, setCarbsRDA] = useState("—");
  const [fatRDA, setFatRDA] = useState("—");
  const [fiberRDA, setFiberRDA] = useState("—");
  const [energyRDA, setEnergyRDA] = useState("");

  const carbsError = useMemo(() => {
    const carbsVal = parseFloat(carbsGrams || "0") || 0;
    const fiberVal = parseFloat(fiberGrams || "0") || 0;
    return carbsVal > 0 && fiberVal > 0 && carbsVal - fiberVal < 0;
  }, [carbsGrams, fiberGrams]);

  useEffect(() => {
    if (target) {
      setProteinGrams(target.protein?.toString() || "");
      setCarbsGrams(target.carbs?.toString() || "");
      setFatGrams(target.fat?.toString() || "");
      setFiberGrams(target.fibre?.toString() || "");
      if (target.recommended_calories) {
        setEnergyRDA(target.recommended_calories.toString());
      }
    }
  }, [target]);

  const updateTargetValues = useCallback(() => {
    try {
      const energyRDAValue = parseFloat(energyRDA || "0") || 0;
      const fatsValue = parseFloat(fatGrams || "0") || 0;
      const fibresValue = parseFloat(fiberGrams || "0") || 0;
      const proteinValue = parseFloat(proteinGrams || "0") || 0;
      const carbsValue = parseFloat(carbsGrams || "0") || 0;
      const weightValue = profile?.weight || 0;

      const totalEnergy = Math.round(
        fatsGramsToKcal(fatsValue) +
          fiberGramsToKcal(fibresValue) +
          proteinsGramsToKcal(proteinValue) +
          carbsGramsToKcal(carbsValue - fibresValue)
      );
      setEnergyValue(totalEnergy.toString());

      const energy = totalEnergy || 0;
      const pct = (kcalShare: number) =>
        energy === 0 ? "—" : `${Math.round((kcalShare * 100) / energy)}%`;

      setFatE(pct(fatsGramsToKcal(fatsValue)));
      setFiberE(pct(fiberGramsToKcal(fibresValue)));
      setProteinE(pct(proteinsGramsToKcal(proteinValue)));
      setCarbsE(pct(carbsGramsToKcal(carbsValue - fibresValue)));

      setEnergyE(
        energyRDAValue === 0
          ? "—"
          : `${Math.round((100 * energy) / energyRDAValue)}%`
      );

      const proteinRDALow = Math.round(0.83 * weightValue);
      const proteinRDAHigh =
        energyRDAValue === 0 ? 0 : Math.round((0.15 * energyRDAValue) / 4);
      setProteinRDA(
        proteinRDAHigh ? `${proteinRDALow} – ${proteinRDAHigh} g` : "—"
      );

      setCarbsRDA(
        energyRDAValue === 0
          ? "—"
          : `${Math.round((0.45 * energyRDAValue) / 4)} – ${Math.round(
              (0.65 * energyRDAValue) / 4
            )} g`
      );

      setFiberRDA(
        energyRDAValue === 0
          ? "—"
          : `${Math.round((15 * energyRDAValue) / 1000)} g`
      );

      setFatRDA(
        energyRDAValue === 0
          ? "—"
          : `${Math.round((0.15 * energyRDAValue) / 9)} – ${Math.round(
              (0.35 * energyRDAValue) / 9
            )} g`
      );
    } catch (e) {
      console.error("Error in updateTargetValues:", e);
      setEnergyValue("0");
      setFatE("—");
      setFiberE("—");
      setProteinE("—");
      setCarbsE("—");
      setEnergyE("—");
      setProteinRDA("—");
      setCarbsRDA("—");
      setFiberRDA("—");
      setFatRDA("—");
    }
  }, [
    proteinGrams,
    carbsGrams,
    fatGrams,
    fiberGrams,
    energyRDA,
    profile?.weight,
  ]);

  useEffect(() => {
    updateTargetValues();
  }, [updateTargetValues]);

  // Baseline strings (what the persisted target looks like in input form).
  // Empty string when the field is unset, so dirty detection is just string equality.
  const baseline = useMemo(
    () => ({
      protein: target?.protein != null ? target.protein.toString() : "",
      carbs: target?.carbs != null ? target.carbs.toString() : "",
      fat: target?.fat != null ? target.fat.toString() : "",
      fibre: target?.fibre != null ? target.fibre.toString() : "",
      recommended_calories:
        target?.recommended_calories != null
          ? target.recommended_calories.toString()
          : "",
    }),
    [target]
  );

  const isDirty =
    !isCompleted &&
    (proteinGrams !== baseline.protein ||
      carbsGrams !== baseline.carbs ||
      fatGrams !== baseline.fat ||
      fiberGrams !== baseline.fibre ||
      energyRDA !== baseline.recommended_calories);

  useDirtyFlag(
    `macronutrient-target:${appointmentId}`,
    isDirty,
    "Macronutrient Targets"
  );

  const handleSave = useCallback(() => {
    if (!isDirty || carbsError) return;
    const data: Partial<NutritionTarget> = {
      ...target,
      protein: parseFloat(proteinGrams) || undefined,
      carbs: parseFloat(carbsGrams) || undefined,
      fat: parseFloat(fatGrams) || undefined,
      fibre: parseFloat(fiberGrams) || undefined,
      energy: parseFloat(energyValue) || undefined,
      recommended_calories: parseFloat(energyRDA) || undefined,
    };
    delete data.id;
    updateTarget.mutate({ data, isNew: !target });
  }, [
    isDirty,
    carbsError,
    proteinGrams,
    carbsGrams,
    fatGrams,
    fiberGrams,
    energyValue,
    energyRDA,
    updateTarget,
    target,
  ]);

  if (isLoading) {
    return (
      <Widget>
        <WidgetContent className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </WidgetContent>
      </Widget>
    );
  }

  const macroColumns = ["Protein", "Carbs", "Fat", "Fibre", "Energy"];
  // Tailwind variants used to draw vertical dividers between cells inside a row,
  // skipping the first cell. Pulled out to keep each row className readable.
  const cellDividers = "[&>*+*]:border-l [&>*+*]:border-border";

  return (
    <Widget>
      <WidgetHeader title="Macronutrient Targets" hasInfo />
      <WidgetContent className="px-0 pb-0">
        <div className="mx-3 overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow
                className={cn(
                  "bg-muted/40 hover:bg-muted/40 border-b-border",
                  cellDividers
                )}
              >
                <TableHead className="h-11 w-[180px]" />
                {macroColumns.map((col) => (
                  <TableHead
                    key={col}
                    className="h-11 text-center z-label text-foreground"
                  >
                    {col}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Set Value (g) — input row. All macros are inputs; Energy is computed read-only. */}
              <TableRow
                className={cn(
                  "bg-primary/5 hover:bg-primary/5 border-b-border",
                  cellDividers
                )}
              >
                <TableCell className="h-12 px-4">
                  <span className="z-eyebrow text-muted-foreground">
                    Set Value
                  </span>
                </TableCell>
                <TableCell className="h-12 p-0">
                  <CellInput
                    ariaLabel="Protein in grams"
                    value={proteinGrams}
                    onChange={setProteinGrams}
                    unit="g"
                    disabled={isCompleted}
                  />
                </TableCell>
                <TableCell className="h-12 p-0">
                  <CellInput
                    ariaLabel="Carbs in grams"
                    value={carbsGrams}
                    onChange={setCarbsGrams}
                    unit="g"
                    disabled={isCompleted}
                    hasError={carbsError}
                  />
                </TableCell>
                <TableCell className="h-12 p-0">
                  <CellInput
                    ariaLabel="Fat in grams"
                    value={fatGrams}
                    onChange={setFatGrams}
                    unit="g"
                    disabled={isCompleted}
                  />
                </TableCell>
                <TableCell className="h-12 p-0">
                  <CellInput
                    ariaLabel="Fibre in grams"
                    value={fiberGrams}
                    onChange={setFiberGrams}
                    unit="g"
                    disabled={isCompleted}
                  />
                </TableCell>
                <TableCell className="h-12 text-center">
                  <DisplayCell value={energyValue} unit="kcal" />
                </TableCell>
              </TableRow>

              {/* %Energy — display row */}
              <TableRow
                className={cn(
                  "hover:bg-transparent border-b-border",
                  cellDividers
                )}
              >
                <TableCell className="h-11 px-4">
                  <span className="z-eyebrow text-muted-foreground">
                    % Energy
                  </span>
                </TableCell>
                {[proteinE, carbsE, fatE, fiberE, energyE].map((v, i) => (
                  <TableCell key={i} className="h-11 text-center">
                    <DisplayCell value={v} />
                  </TableCell>
                ))}
              </TableRow>

              {/* RDA — display row, with editable Energy RDA driving downstream calcs */}
              <TableRow className={cn("hover:bg-transparent", cellDividers)}>
                <TableCell className="h-12 px-4">
                  <span className="z-eyebrow text-muted-foreground">RDA</span>
                </TableCell>
                {[proteinRDA, carbsRDA, fatRDA, fiberRDA].map((v, i) => (
                  <TableCell key={i} className="h-12 text-center">
                    <DisplayCell value={v} />
                  </TableCell>
                ))}
                <TableCell className="h-12 p-0">
                  <CellInput
                    ariaLabel="Energy RDA in kcal"
                    value={energyRDA}
                    onChange={setEnergyRDA}
                    unit="kcal"
                    disabled={isCompleted}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {carbsError && (
          <p className="z-hint text-destructive px-3 pt-3">
            Carbs must be greater than or equal to fibre.
          </p>
        )}

        {!isCompleted && (
          <div className="flex items-center justify-end gap-3 px-3 pb-3 pt-3">
            {isDirty && (
              <span className="z-hint text-muted-foreground">
                Unsaved changes
              </span>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || carbsError || updateTarget.isPending}
            >
              {updateTarget.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Save Targets
            </Button>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const SetTargetTab: React.FC = () => {
  return (
    <div className="space-y-4">
      <SpecialFeedConsiderationCard />
      <MealStrategyCard />
      {/* V2 split layout. To revert: replace this provider+two-card block with
          <WeightEnergyTargetsCard /> + <MacronutrientTargetsCard />. */}
      <MacronutrientTargetsProvider>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WeightEnergyTargetsCard />
          <TargetEnergyCard />
        </div>
        <MacroInputsCard />
      </MacronutrientTargetsProvider>
    </div>
  );
};
